import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Link di verifica non valido');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await fetch(`${API}/auth/verify-email?token=${token}`, {
        credentials: 'include'
      });
      
      let data;
      try {
        data = await response.json();
      } catch {
        data = { detail: 'Errore nel parsing della risposta' };
      }
      
      if (response.ok) {
        setStatus('success');
        setMessage('Email verificata con successo!');
        
        // Auto-login user
        if (data.session_token) {
          localStorage.setItem('session_token', data.session_token);
          login(data);
        }
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        const errorMessage = data.detail || 'Link di verifica non valido o già utilizzato';
        if (response.status === 400 && errorMessage.toLowerCase().includes('scaduto')) {
          setStatus('expired');
          setMessage(errorMessage);
        } else {
          setStatus('error');
          setMessage(errorMessage);
        }
      }
    } catch (error) {
      console.error('Verify email error:', error);
      setStatus('error');
      setMessage('Impossibile connettersi al server. Riprova.');
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    setResending(true);
    try {
      const response = await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Se l\'email esiste, riceverai un nuovo link di verifica');
        setStatus('resent');
      } else {
        if (response.status === 410) {
          // Account deleted due to expiration
          toast.error(data.detail);
          setTimeout(() => navigate('/', { replace: true }), 2000);
        } else {
          toast.error(data.detail || 'Errore nell\'invio');
        }
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] to-white dark:from-[#1D1D1F] dark:to-black flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
            <span className="text-3xl">🐾</span>
          </div>
          <h1 className="text-2xl font-bold text-[#1D1D1F] dark:text-white">Zampa</h1>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-[#2D2D2F] rounded-3xl p-8 shadow-xl">
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-[#FF6B4A] animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-[#1D1D1F] dark:text-white mb-2">
                Verifica in corso...
              </h2>
              <p className="text-[#6E6E73] dark:text-gray-400">
                Stiamo verificando la tua email
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full mx-auto mb-4 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-[#1D1D1F] dark:text-white mb-2">
                Email Verificata!
              </h2>
              <p className="text-[#6E6E73] dark:text-gray-400 mb-4">
                {message}
              </p>
              <p className="text-sm text-[#FF6B4A]">
                Reindirizzamento alla dashboard...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full mx-auto mb-4 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-[#1D1D1F] dark:text-white mb-2">
                Verifica Fallita
              </h2>
              <p className="text-[#6E6E73] dark:text-gray-400 mb-6">
                {message}
              </p>
              <Button
                onClick={() => navigate('/', { replace: true })}
                className="bg-[#1D1D1F] dark:bg-white hover:bg-black dark:hover:bg-gray-100 text-white dark:text-[#1D1D1F] rounded-xl px-6"
              >
                Torna alla Home
              </Button>
            </div>
          )}

          {(status === 'expired' || status === 'resent') && (
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Mail className="w-12 h-12 text-orange-500" />
              </div>
              <h2 className="text-xl font-semibold text-[#1D1D1F] dark:text-white mb-2">
                {status === 'resent' ? 'Link Inviato!' : 'Link Scaduto'}
              </h2>
              <p className="text-[#6E6E73] dark:text-gray-400 mb-6">
                {status === 'resent' 
                  ? 'Controlla la tua casella email per il nuovo link di verifica.'
                  : 'Il link di verifica è scaduto. Richiedi un nuovo link inserendo la tua email.'}
              </p>
              
              {status === 'expired' && (
                <div className="space-y-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="La tua email"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1D1D1F] text-[#1D1D1F] dark:text-white focus:ring-2 focus:ring-[#FF6B4A] focus:border-transparent"
                  />
                  <Button
                    onClick={handleResend}
                    disabled={resending}
                    className="w-full bg-[#FF6B4A] hover:bg-[#E55A3A] text-white rounded-xl py-3"
                  >
                    {resending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      'Invia nuovo link'
                    )}
                  </Button>
                </div>
              )}
              
              <Button
                variant="ghost"
                onClick={() => navigate('/', { replace: true })}
                className="mt-4 text-[#6E6E73] hover:text-[#1D1D1F]"
              >
                Torna alla Home
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
